import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { BurpRequestResponse } from '../../types';
import { burpService } from '../../services/burpService';
import { useNotification } from '../../hooks/NotificationContext';

interface BurpRequestResponseSectionProps {
  schwachstelleId: number | null;
  editable?: boolean;
  onInsertCodeBlock?: (pairId: number) => void;
}

const BurpRequestResponseSection: React.FC<BurpRequestResponseSectionProps> = ({
  schwachstelleId,
  editable = false,
  onInsertCodeBlock,
}) => {
  const { showError, showSuccess } = useNotification();
  const [items, setItems] = useState<BurpRequestResponse[]>([]);
  const [loading, setLoading] = useState(false);

  const loadItems = useCallback(async () => {
    if (!schwachstelleId) return;
    try {
      setLoading(true);
      const data = await burpService.listPairs({ schwachstelle_id: schwachstelleId });
      setItems(data);
    } catch (error) {
      console.error('Fehler beim Laden der Burp-Paare:', error);
      showError('Burp Request/Response konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [schwachstelleId, showError]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleCopy = async (pairId: number) => {
    try {
      const content = await burpService.getCombinedContent(pairId);
      await navigator.clipboard.writeText(content);
      showSuccess('Request/Response in Zwischenablage kopiert');
    } catch {
      showError('Kopieren fehlgeschlagen');
    }
  };

  if (!schwachstelleId) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
            Burp Request/Response
          </Typography>
          <Alert severity="info">Bitte zuerst speichern, um Burp-Einträge anzuzeigen.</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="caption" sx={{ display: 'block', mb: 1.5 }}>
          Burp Request/Response
        </Typography>

        {loading && (
          <Box sx={{ display: 'grid', gap: 1.25 }}>
            {Array.from({ length: 3 }).map((_, idx) => (
              <Skeleton key={idx} variant="rounded" height={64} />
            ))}
          </Box>
        )}

        {!loading && items.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Keine Burp Request/Response-Paare vorhanden.
          </Typography>
        )}

        {!loading && items.length > 0 && (
          <Box sx={{ display: 'grid', gap: 1 }}>
            {items.map((item) => (
              <Box
                key={item.id}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {item.method} {item.url}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.title || 'Ohne Titel'}
                    {item.timestamp ? ` | ${item.timestamp}` : ''}
                  </Typography>
                </Box>

                {editable && onInsertCodeBlock && (
                  <Tooltip title="Als Code-Block in Editor einfügen" arrow>
                    <IconButton size="small" onClick={() => onInsertCodeBlock(item.id)}>
                      <CodeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title="In Zwischenablage kopieren" arrow>
                  <IconButton size="small" onClick={() => handleCopy(item.id)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default BurpRequestResponseSection;
